import toast from 'react-hot-toast';
import {
  Button,
  Sheet,
  SheetContent,
  Heading,
  Paragraph,
} from '@/components/atoms';
import { downloadBillingReceipt } from '@/lib/billing/download-receipt';
import type { BillingHistoryItem } from './billing-table';

interface ReceiptDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  billingItem: BillingHistoryItem | null;
}

export const ReceiptDetailSheet = ({
  open,
  onOpenChange,
  billingItem,
}: ReceiptDetailSheetProps) => {
  if (!billingItem) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <div className="flex flex-col gap-8 p-4 md:p-6 lg:p-8">
          {/* Receipt Number and Date */}
          <div className="flex flex-col items-end gap-2">
            <Paragraph size="sm" className="text-sm">
              <span className="font-semibold">Receipt No:</span> SEL-2025-0893
            </Paragraph>
            <Paragraph size="sm" className="text-sm">
              <span className="font-semibold">Date:</span> 19 Aug 2025
            </Paragraph>
          </div>

          {/* Title Section */}
          <div className="flex flex-col gap-2">
            <Paragraph size="sm" className="text-sm text-muted-foreground">
              July Receipt of payment
            </Paragraph>
            <Heading size="xl" className="text-primary">
              Sellyt Payment Receipt
            </Heading>
          </div>

          <div className="border-t border-accent" />

          {/* Plan Details */}
          <div className="flex flex-col gap-6">
            <Heading size="sm">Plan Details:</Heading>

            <div className="flex flex-col gap-4">
              <div className="flex justify-between">
                <Paragraph size="sm" className="text-muted-foreground">
                  Plan:
                </Paragraph>
                <Paragraph size="sm" className="font-medium">
                  Growth Plan
                </Paragraph>
              </div>
              <div className="flex justify-between">
                <Paragraph size="sm" className="text-muted-foreground">
                  Billing Cycle:
                </Paragraph>
                <Paragraph size="sm" className="font-medium">
                  Monthly
                </Paragraph>
              </div>
              <div className="flex justify-between">
                <Paragraph size="sm" className="text-muted-foreground">
                  Start Date:
                </Paragraph>
                <Paragraph size="sm" className="font-medium">
                  {billingItem.startDate}
                </Paragraph>
              </div>
              <div className="flex justify-between">
                <Paragraph size="sm" className="text-muted-foreground">
                  Expiry Date:
                </Paragraph>
                <Paragraph size="sm" className="font-medium">
                  {billingItem.endDate}
                </Paragraph>
              </div>
            </div>
          </div>

          <div className="border-t border-accent" />

          {/* Payment Summary */}
          <div className="flex flex-col gap-6">
            <Heading size="sm">Payment Summary:</Heading>

            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 pb-3 border-b border-accent">
              <Paragraph size="sm" className="font-semibold">
                Description
              </Paragraph>
              <Paragraph size="sm" className="font-semibold text-center">
                Qty
              </Paragraph>
              <Paragraph size="sm" className="font-semibold text-center">
                Price (N)
              </Paragraph>
              <Paragraph size="sm" className="font-semibold text-right">
                Total (N)
              </Paragraph>
            </div>

            {/* Table Row */}
            <div className="grid grid-cols-4 gap-4 py-2">
              <Paragraph size="sm">Growth Plan (Monthly)</Paragraph>
              <Paragraph size="sm" className="text-center">
                1
              </Paragraph>
              <Paragraph size="sm" className="text-center">
                N5,000
              </Paragraph>
              <Paragraph size="sm" className="text-right">
                N5,000
              </Paragraph>
            </div>

            <div className="border-t border-accent" />

            {/* Subtotal and VAT */}
            <div className="flex flex-col gap-3">
              <div className="flex justify-between">
                <Paragraph size="sm">Subtotal: N5,000</Paragraph>
              </div>
              <div className="flex justify-between">
                <Paragraph size="sm">VAT (7.5%): N562.50</Paragraph>
              </div>
            </div>

            <div className="border-t border-accent" />

            {/* Total Paid */}
            <div className="flex justify-between items-center py-3">
              <Heading size="sm">Total Paid:</Heading>
              <Heading size="sm" className="text-primary">
                N5,562.50
              </Heading>
            </div>

            <div className="border-t border-accent" />

            {/* Payment Method */}
            <div className="flex justify-between py-3">
              <Paragraph size="sm" className="font-semibold">
                Payment Method:
              </Paragraph>
              <Paragraph size="sm">{billingItem.paymentMethod}</Paragraph>
            </div>
          </div>

          {/* Download Button */}
          <Button
            className="w-full mt-4"
            size="lg"
            disabled={billingItem.status !== 'Paid'}
            onClick={async () => {
              if (billingItem.status !== 'Paid') {
                toast.error('Receipt is only available for paid invoices.');
                return;
              }
              try {
                await downloadBillingReceipt({
                  invoiceId: billingItem.invoiceId,
                  plan: billingItem.plan,
                  amount: billingItem.rawAmount,
                  currency: billingItem.currency,
                  paymentMethod: billingItem.paymentMethod,
                  status: billingItem.status,
                  startDate: billingItem.rawStartDate,
                  endDate: billingItem.rawEndDate,
                  autoRenew: billingItem.autoRenew,
                  paidAt: billingItem.paidAt,
                  receiptUrl: billingItem.receiptUrl,
                });
              } catch {
                toast.error('Could not download receipt. Please try again.');
              }
            }}
          >
            Download receipt
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
